import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { useBuInstanceLabel } from "@/hooks/use-bu-instance-label";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type ExtractedValue, useExtractedValues } from "@/hooks/use-master-data-extracted-values";
import type { MasterDataEntityType } from "@/lib/enums";

interface MasterDataValueSelectProps {
  value: string | null;
  onChange: (value: string) => void;
  /** Which BU's extracted-value cache to search — the source or target side. */
  buInstanceId: string | null;
  entityType: MasterDataEntityType;
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Server-side-searchable single-select for a Coach Manager master-data
 * reference value, backed by `master_data_extracted_value` (the only source
 * of candidate values — Coach Manager has no list operation). Mirrors
 * `WidgetSelect.tsx`'s `AsyncCombobox`-wrapping pattern, but allows free-text
 * entry via `allowCustomValue`/`getCustomOption`: real Coach Manager values
 * may not have been seen in a search yet, so this must never be pick-only.
 */
export function MasterDataValueSelect({
  value,
  onChange,
  buInstanceId,
  entityType,
  selectedLabel,
  disabled,
  className,
}: MasterDataValueSelectProps) {
  const { label } = useBuInstanceLabel();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useExtractedValues({
    buInstanceId: buInstanceId ?? "",
    entityType,
    search: debouncedSearch,
  });
  const values = data?.data ?? [];

  return (
    <AsyncCombobox<ExtractedValue>
      value={value}
      onChange={(newValue) => onChange(newValue)}
      options={values}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.value}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={isFetching}
      disabled={disabled || !buInstanceId}
      className={className}
      placeholder={buInstanceId ? "Select or type a value…" : `Pick a ${label} first…`}
      searchPlaceholder="Search known values…"
      emptyText="No known values yet — type to enter one."
      allowCustomValue
      getCustomOption={(text) => ({ value: text })}
    />
  );
}
